import { useState, useEffect } from "react";
import api from "../api/client";

export default function WelcomeBriefing({ onClose, onOpenAI }) {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBriefing();
  }, []);

  const fetchBriefing = async () => {
    try {
      const res = await api.get("/ai/welcome-briefing");
      setBriefing(res.data);
    } catch (e) {
      setError("Impossible de charger le briefing");
      console.error("Briefing error:", e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full animate-fade-in text-center shadow-xl">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#1a2744]/10 to-[#d4a012]/10 border border-[#1a2744]/20 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-[#1a2744]/20 border-t-[#1a2744] rounded-full animate-spin" />
          </div>
          <p className="text-gray-500 text-sm">Préparation de votre briefing personnalisé...</p>
        </div>
      </div>
    );
  }

  if (error || !briefing) {
    return null; // Silently fail
  }

  const data = briefing.data || {};
  const hasOverdue = data.overdue_steps?.length > 0;
  const hasDueSoon = data.due_soon_steps?.length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 lg:p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full animate-fade-in-up overflow-hidden max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="welcome-briefing-modal">
        {/* Header with gradient */}
        <div className="relative p-4 lg:p-6 pb-3 lg:pb-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a2744]/5 via-[#d4a012]/5 to-transparent" />
          <div className="relative flex items-start gap-3 lg:gap-4">
            <div className="w-11 h-11 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-gradient-to-br from-[#1a2744] to-[#2d3e5f] flex items-center justify-center shadow-lg flex-shrink-0">
              <svg className="w-5 h-5 lg:w-7 lg:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg lg:text-xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                Briefing du jour
              </h2>
              <p className="text-xs lg:text-sm text-gray-500 mt-0.5 lg:mt-1">
                Votre assistant IA vous résume la situation
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 lg:p-2 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              data-testid="close-briefing-btn"
            >
              <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Briefing Content */}
        <div className="px-4 lg:px-6 pb-3 lg:pb-4">
          <div className="p-3 lg:p-4 bg-gray-50 rounded-xl border border-gray-200">
            <p className="text-xs lg:text-sm text-gray-700 leading-relaxed whitespace-pre-wrap" data-testid="briefing-text">
              {briefing.briefing}
            </p>
          </div>
        </div>

        {/* Stats Summary */}
        {data.role === "student" && (
          <div className="px-4 lg:px-6 pb-3 lg:pb-4">
            <div className="grid grid-cols-4 gap-1.5 lg:gap-2">
              <div className="text-center p-2 lg:p-3 bg-gray-50 rounded-xl border border-gray-100">
                <span className="text-lg lg:text-2xl font-bold text-[#1a2744]" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {data.progress || 0}%
                </span>
                <p className="text-[10px] lg:text-xs text-gray-500 mt-0.5 lg:mt-1">Progression</p>
              </div>
              <div className="text-center p-2 lg:p-3 bg-gray-50 rounded-xl border border-gray-100">
                <span className="text-lg lg:text-2xl font-bold text-emerald-600" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {data.completed_steps || 0}
                </span>
                <p className="text-[10px] lg:text-xs text-gray-500 mt-0.5 lg:mt-1">Complétées</p>
              </div>
              <div className="text-center p-2 lg:p-3 bg-gray-50 rounded-xl border border-gray-100">
                <span className="text-lg lg:text-2xl font-bold text-cyan-600" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {data.in_progress_steps || 0}
                </span>
                <p className="text-[10px] lg:text-xs text-gray-500 mt-0.5 lg:mt-1">En cours</p>
              </div>
              <div className="text-center p-2 lg:p-3 bg-gray-50 rounded-xl border border-gray-100">
                <span className="text-lg lg:text-2xl font-bold text-amber-600" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {data.total_visits || 0}
                </span>
                <p className="text-[10px] lg:text-xs text-gray-500 mt-0.5 lg:mt-1">Visites</p>
              </div>
            </div>
          </div>
        )}

        {/* Alerts */}
        {(hasOverdue || hasDueSoon) && (
          <div className="px-4 lg:px-6 pb-3 lg:pb-4 space-y-2">
            {hasOverdue && (
              <div className="flex items-center gap-2 lg:gap-3 p-2.5 lg:p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <svg className="w-4 h-4 lg:w-5 lg:h-5 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-xs lg:text-sm font-medium text-red-400">{data.overdue_steps.length} étape(s) en retard</p>
                  <p className="text-[10px] lg:text-xs text-red-400/70 truncate">{data.overdue_steps.map(s => s.title).join(", ")}</p>
                </div>
              </div>
            )}
            {hasDueSoon && (
              <div className="flex items-center gap-2 lg:gap-3 p-2.5 lg:p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                <svg className="w-4 h-4 lg:w-5 lg:h-5 text-amber-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-xs lg:text-sm font-medium text-amber-400">{data.due_soon_steps.length} échéance(s) proche(s)</p>
                  <p className="text-[10px] lg:text-xs text-amber-400/70 truncate">{data.due_soon_steps.map(s => s.title).join(", ")}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="p-4 lg:p-6 pt-2 flex gap-2 lg:gap-3">
          <button
            onClick={onClose}
            className="flex-1 btn-ghost py-2.5 lg:py-3 text-sm"
            data-testid="continue-btn"
          >
            Continuer
          </button>
          <button
            onClick={() => { onClose(); onOpenAI?.(); }}
            className="flex-1 btn-primary py-2.5 lg:py-3 flex items-center justify-center gap-2 text-sm"
            data-testid="open-ai-btn"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span className="hidden sm:inline">Accéder à Odys'IA</span>
            <span className="sm:hidden">Odys'IA</span>
            <svg className="w-3.5 h-3.5 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
